import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SalesPersonToken } from '@sleed/bizcommerce-api';
import { salesPersonsApi } from '~/lib/api/client';
import { formToToken, type SalesPersonFormValues } from '~/lib/schemas/sales-person';

export const salesPersonKeys = {
	all: ['sales-persons'] as const,
	list: () => [...salesPersonKeys.all, 'list'] as const
};

/** All sales persons (client-side grid; legacy loads the full set via GetAll). */
export function useSalesPersonsList() {
	return useQuery<SalesPersonToken[]>({
		queryKey: salesPersonKeys.list(),
		queryFn: async () => (await salesPersonsApi.getAll()).data.items ?? []
	});
}

export function useSalesPersonMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: salesPersonKeys.all });
	const create = useMutation({
		mutationFn: async (form: SalesPersonFormValues) => (await salesPersonsApi.insert(formToToken(form, null))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async ({ form, original }: { form: SalesPersonFormValues; original: SalesPersonToken | null }) =>
			(await salesPersonsApi.update(formToToken(form, original))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await salesPersonsApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
